import {
  ConvertedDateType,
  DatePickerMode,
  DatePickerValue,
  IncomingDatePickerValue,
  RangeIncomingDateType,
  SingleIncomingDateType,
  SplitModeRole,
} from "@/components/datePicker/DatePicker.types.ts";
import {DateFormatName, DatePickerFormat, TimeFormatName, TimePickerFormat} from "@/lib/types/common.ts";
import {format, toDate} from "date-fns-tz";
import {DateRange, TZDate} from "react-day-picker";

export const formatInputDate = (
  date: Date,
  dateFormat?: DateFormatName,
  timeFormat?: TimeFormatName,
  timezone?: string | undefined
) => {
  if (timezone) {
    date = new TZDate(date, timezone);
  }

  if (!date) return "";

  const datePattern = DatePickerFormat[dateFormat ?? DateFormatName.DEFAULT];
  // Only append the time part (and its separating space) when a time format is set —
  // otherwise the pattern leaves a trailing space that is awkward to select/delete.
  const pattern = timeFormat ? `${datePattern} ${TimePickerFormat[timeFormat]}` : datePattern;
  return format(date, pattern);
};

export const dateToInputValue = <Mode extends DatePickerMode>(
  date: DatePickerValue<Mode>,
  dateFormat: DateFormatName,
  timeFormat: TimeFormatName | undefined,
  mode: Mode,
  timezone: string | undefined,
  splitRole?: SplitModeRole
) => {
  if (!date) return "";
  if (mode === "range" && ("from" in date || "to" in date)) {
    return `${"from" in date && date.from instanceof Date ? formatInputDate(date.from, dateFormat, timeFormat, timezone) : ""} - ${"to" in date && date.to instanceof Date ? formatInputDate(date.to, dateFormat, timeFormat, timezone) : ""}`;
  } else if (mode === "split") {
    if (splitRole === "end" && "to" in date && date.to) {
      return formatInputDate(date.to, dateFormat, timeFormat, timezone);
    }
    if (splitRole === "start" && "from" in date && date.from) {
      return formatInputDate(date.from, dateFormat, timeFormat, timezone);
    }
  } else if (date instanceof Date) {
    return formatInputDate(date, dateFormat, timeFormat, timezone);
  }
  return "";
};

export const parseInputForDate = (
  input: string,
  mode: DatePickerMode,
  timezone: string | undefined
): Date | DateRange | undefined => {
  const parts = input.split("-").map((part) => part.trim());
  if (mode === "single" || mode === "split") {
    return parseToAccountTimeZoneInvalidDate(parts[0], timezone);
  } else if (mode === "range") {
    if (parts.length !== 2) {
      return undefined;
    }
    const from = parseToAccountTimeZoneInvalidDate(parts[0], timezone);
    const to = parseToAccountTimeZoneInvalidDate(parts[1], timezone);
    if (!from && !to) {
      return undefined;
    }
    return {from, to};
  }
  return undefined;
};

export const parseToAccountTimeZoneRaisesError = (date: string | undefined, timezone: string | undefined) => {
  if (!date) return undefined;
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) throw new Error("Invalid date");
  if (timezone) {
    const dateWithoutTimezone = format(parsedDate, "yyyy-MM-dd HH:mm:ss");
    const tzDate = toDate(dateWithoutTimezone, {timeZone: timezone});
    return new TZDate(tzDate, timezone);
  }
  return parsedDate;
};

export const parseToAccountTimeZoneInvalidDate = (date: string | undefined, timezone: string | undefined) => {
  try {
    return parseToAccountTimeZoneRaisesError(date, timezone);
  } catch {
    return new Date("Invalid Date");
  }
};

export const parseToAccountTimeZone = (date: string | undefined, timezone: string | undefined) => {
  try {
    return parseToAccountTimeZoneRaisesError(date, timezone);
  } catch {
    return undefined;
  }
};

export const TZDateOrNow = (date: Date | string | undefined, timezone: string | undefined): TZDate | Date => {
  if (!timezone) {
    return date ? new Date(date) : new Date();
  }
  if (date) {
    return new TZDate(new Date(date), timezone);
  }
  return new TZDate(new Date(), timezone);
};

const TZDateConstructorTypeGuard = (date: Date | number | string, timezone?: string): TZDate => {
  let tzdate: TZDate | undefined = undefined;
  if (date instanceof TZDate) {
    tzdate = date;
  }
  if (typeof date === "string") {
    tzdate = new TZDate(date as string, timezone);
  }
  if (typeof date === "number") {
    tzdate = new TZDate(date as number, timezone);
  }
  if (date instanceof Date) {
    tzdate = new TZDate(date, timezone);
  }
  if (!tzdate) {
    throw new Error("Invalid date type provided to TZDateTypeGuard");
  }
  if (isNaN(tzdate.getTime())) {
    throw new Error("Invalid date provided");
  }
  return tzdate;
};

export const convertIncomingToTZDate = (
  incomingDate: SingleIncomingDateType | RangeIncomingDateType,
  timezone?: string
): ConvertedDateType => {
  if (
    typeof incomingDate === "string" ||
    typeof incomingDate === "number" ||
    incomingDate instanceof Date ||
    incomingDate === null
  ) {
    if (!incomingDate) {
      return undefined;
    }
    try {
      return TZDateConstructorTypeGuard(incomingDate, timezone);
    } catch (error) {
      console.error("Invalid date string provided:", incomingDate, error);
      return undefined;
    }
  }
  if (typeof incomingDate === "object" && "to" in incomingDate && "from" in incomingDate) {
    let from = incomingDate.from;
    let to = incomingDate.to;
    if (
      incomingDate.from &&
      typeof (
        incomingDate.from === "string" ||
        incomingDate.from instanceof Date ||
        typeof incomingDate.from === "number"
      )
    ) {
      try {
        from = TZDateConstructorTypeGuard(incomingDate.from, timezone);
      } catch (error) {
        console.error("Invalid 'from' date provided:", incomingDate.from, error);
        return undefined;
      }
    }
    if (
      incomingDate.to &&
      (typeof incomingDate.to === "string" || incomingDate.to instanceof Date || typeof incomingDate.to === "number")
    ) {
      try {
        to = TZDateConstructorTypeGuard(incomingDate.to, timezone);
      } catch (error) {
        console.error("Invalid 'to' date provided:", incomingDate.to, error);
        return undefined;
      }
    }
    const dateRange = {from, to};
    return dateRange as {from: TZDate | undefined; to: TZDate | undefined};
  }
  return incomingDate;
};

export const selectedRangeUseEffectDependency = (selectedProp: IncomingDatePickerValue<DatePickerMode>) => {
  if (selectedProp && selectedProp instanceof Object && "to" in selectedProp && "from" in selectedProp) {
    return [selectedProp.to, selectedProp.from];
  }

  return [selectedProp];
};
